import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Input, Field, Stat } from "@/components/UI";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, CartesianGrid } from "recharts";
import { Plus, Trash, LockKey, Drop, Warning, ChartLineUp } from "@phosphor-icons/react";
import { toast } from "sonner";

function today() { return new Date().toISOString().slice(0, 10); }

/**
 * Süt bölümü (Normal veya Antibiyotikli) — tek bileşen, endpoint prop'a göre değişir.
 */
function MilkSection({
  title,
  subtitle,
  endpoint,          // "/milk" veya "/antibiotic-milk"
  testPrefix,        // "milk" veya "amilk"
  accent,            // { bar, line, bg, ring, badge } renk seti
  canSeePrices,
  variantIcon,
}) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({ date: today(), liters: "", milked_animal_count: "", unit_price: "", notes: "" });

  const load = useCallback(() => {
    api.get(endpoint).then(r => setItems(r.data)).catch(() => {});
    api.get(`${endpoint}/summary?days=30`).then(r => {
      setSummary(r.data);
      if (canSeePrices && r.data.last_unit_price) {
        setForm(f => f.unit_price ? f : { ...f, unit_price: r.data.last_unit_price });
      }
    }).catch(() => {});
  }, [endpoint, canSeePrices]);
  useEffect(() => { load(); }, [load]);

  const litersNum = Number(form.liters || 0);
  const priceNum = Number(form.unit_price || 0);
  const previewRevenue = litersNum * priceNum;

  const submit = async (e) => {
    e.preventDefault();
    if (litersNum <= 0) return toast.error("Litre 0'dan büyük olmalı");
    const body = {
      date: form.date,
      liters: litersNum,
      milked_animal_count: Number(form.milked_animal_count || 0),
      unit_price: canSeePrices ? priceNum : 0,
      notes: form.notes,
    };
    try {
      const r = await api.post(endpoint, body);
      const replaced = r.data?.replaced;
      const labelPrefix = title;
      if (replaced) {
        toast.success(
          canSeePrices && previewRevenue > 0
            ? `${labelPrefix}: ${form.date} tarihli kayıt güncellendi (${litersNum} L, ${previewRevenue.toLocaleString("tr-TR")} ₺)`
            : `${labelPrefix}: ${form.date} tarihli kayıt güncellendi (${litersNum} L)`
        );
      } else {
        toast.success(
          canSeePrices && previewRevenue > 0
            ? `${labelPrefix}: ${litersNum}L × ${priceNum}₺ = ${previewRevenue.toLocaleString("tr-TR")} ₺ Finans'a gelir eklendi`
            : `${labelPrefix}: kayıt eklendi (${litersNum} L)`
        );
      }
      setForm({ ...form, liters: "", milked_animal_count: "", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const del = async (id) => {
    if (!window.confirm("Kayıt silinsin mi?" + (canSeePrices ? " (Bağlı finans geliri de silinir)" : ""))) return;
    try {
      await api.delete(`${endpoint}/${id}`); toast.success("Silindi"); load();
    } catch (err) { toast.error(err?.message || "Hata"); load(); }
  };

  return (
    <div className={`rounded-lg border ${accent.border} overflow-hidden mb-8`} data-testid={`${testPrefix}-section`}>
      <div className={`px-5 py-4 ${accent.bg} border-b ${accent.border} flex items-center gap-3`}>
        <div className={`p-2 rounded-full ${accent.chipBg}`}>
          {variantIcon}
        </div>
        <div>
          <h2 className={`heading text-xl ${accent.title}`}>{title}</h2>
          <p className="text-xs text-[#5e675e]">{subtitle}</p>
        </div>
      </div>

      <div className="p-5 bg-white">
        <div className={`grid gap-3 mb-5 grid-cols-2 md:grid-cols-3 ${canSeePrices ? "lg:grid-cols-5" : ""}`}>
          <Stat testId={`${testPrefix}-total`} label="Son 30 gün toplam" value={summary?.total ?? 0} unit="L" tone="accent" />
          <Stat testId={`${testPrefix}-avg`} label="Günlük Ort." value={summary?.average ?? 0} unit="L" />
          <Stat testId={`${testPrefix}-per-animal`} label="Hayvan Başına Ort." value={summary?.per_animal_avg ?? 0} unit="L/hayvan" tone="accent" />
          {canSeePrices && (
            <>
              <Stat testId={`${testPrefix}-revenue`} label="Son 30 gün gelir" value={(summary?.total_revenue ?? 0).toLocaleString("tr-TR")} unit="₺" tone="ok" />
              <Stat testId={`${testPrefix}-last-price`} label="Son Litre Fiyatı" value={summary?.last_unit_price ?? 0} unit="₺/L" />
            </>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-4 mb-5">
          <Card>
            <h3 className="heading text-base mb-3">
              {canSeePrices ? "30 Günlük Üretim ve Gelir" : "30 Günlük Üretim"}
            </h3>
            <div style={{ height: 220 }}>
              <ResponsiveContainer>
                <LineChart data={summary?.series ?? []}>
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8a938c" }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#8a938c" }} />
                  {canSeePrices && <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#8a938c" }} />}
                  <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC" }} formatter={(v, n) => n === "revenue" ? `${Number(v).toLocaleString("tr-TR")} ₺` : `${v} L`} />
                  <Line yAxisId="left" type="monotone" dataKey="liters" stroke={accent.lineLiters} strokeWidth={2.5} dot={{ r: 3, fill: accent.lineLiters }} name="Litre" />
                  {canSeePrices && (
                    <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#C45B45" strokeWidth={2} dot={{ r: 2, fill: "#C45B45" }} name="Gelir (₺)" />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card>
            <h3 className="heading text-base mb-3">Hayvan Başına Ortalama Süt</h3>
            <div style={{ height: 220 }}>
              <ResponsiveContainer>
                <LineChart data={summary?.series ?? []}>
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8a938c" }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 11, fill: "#8a938c" }} />
                  <Tooltip
                    contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC" }}
                    formatter={(v, n) => n === "per_animal" ? `${v} L/hayvan` : `${v} hayvan`}
                  />
                  <Line type="monotone" dataKey="per_animal" stroke="#DDA74F" strokeWidth={2.5} dot={{ r: 3, fill: "#DDA74F" }} name="L/hayvan" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="text-xs text-[#8a938c] mt-2">
              Günlük süt miktarı ÷ sağılan hayvan sayısı. Sağılan hayvan girilmemiş günler grafiğe girmez.
            </div>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          <Card>
            <h3 className="heading text-base mb-3">Kayıt Ekle</h3>
            <form onSubmit={submit} className="space-y-3">
              <Field label="Tarih *"><Input type="date" data-testid={`${testPrefix}-date`} required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></Field>
              <div className={`grid gap-3 ${canSeePrices ? "grid-cols-2" : "grid-cols-1"}`}>
                <Field label="Miktar (L) *"><Input type="number" step="0.1" min="0.1" data-testid={`${testPrefix}-liters`} required value={form.liters} onChange={e => setForm({ ...form, liters: e.target.value })} /></Field>
                {canSeePrices && (
                  <Field label="Litre Fiyatı (₺) *"><Input type="number" step="0.01" min="0" data-testid={`${testPrefix}-price`} required value={form.unit_price} onChange={e => setForm({ ...form, unit_price: e.target.value })} placeholder="Örn: 22" /></Field>
                )}
              </div>
              <Field label="Toplam Sağılan Hayvan">
                <Input type="number" min="0" data-testid={`${testPrefix}-count`} value={form.milked_animal_count} onChange={e => setForm({ ...form, milked_animal_count: e.target.value })} placeholder="Örn: 15" />
              </Field>
              <Field label="Notlar"><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>

              {litersNum > 0 && Number(form.milked_animal_count || 0) > 0 && (
                <div className="text-xs bg-[#FEF6E0] border border-[#EED7A6] text-[#8B6914] px-3 py-2 rounded-md" data-testid={`${testPrefix}-per-animal-preview`}>
                  🐄 {litersNum} L ÷ {form.milked_animal_count} hayvan = <b>{(litersNum / Number(form.milked_animal_count)).toFixed(2)} L/hayvan</b>
                </div>
              )}
              {canSeePrices && litersNum > 0 && priceNum > 0 && (
                <div className="text-xs bg-[#E4EEE6] border border-[#c2d6c8] text-[#2D4A36] px-3 py-2 rounded-md" data-testid={`${testPrefix}-preview`}>
                  ✓ {litersNum} L × {priceNum} ₺ = <b>{previewRevenue.toLocaleString("tr-TR")} ₺</b> Finans'a gelir olarak eklenecek
                </div>
              )}
              {!canSeePrices && (
                <div className="text-xs bg-[#FAF8F4] border border-[#EEECE5] text-[#5e675e] px-3 py-2 rounded-md flex items-center gap-2" data-testid={`${testPrefix}-price-hidden`}>
                  <LockKey size={14} className="text-[#8B6914]" />
                  Fiyat ve gelir bilgileri sistemde otomatik hesaplanır (yönetim tarafından görülebilir).
                </div>
              )}
              <div className="text-xs text-[#5e675e]">
                💡 Günde tek kayıt — aynı tarihe yeni kayıt girerseniz mevcut kayıt{canSeePrices ? " (ve bağlı finans satırı)" : ""} güncellenir.
              </div>

              <Button type="submit" data-testid={`${testPrefix}-submit`}><Plus size={14} className="inline mr-1" /> Kaydet</Button>
            </form>
          </Card>

          <Card className="lg:col-span-2 !p-0 overflow-hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th className="text-right">Hayvan</th>
                  <th className="text-right">Litre</th>
                  <th className="text-right">L/Hayvan</th>
                  {canSeePrices && <th className="text-right">Fiyat</th>}
                  {canSeePrices && <th className="text-right">Gelir</th>}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.slice(0, 30).map(x => {
                  const perAn = x.milked_animal_count > 0 ? (Number(x.liters) / x.milked_animal_count) : 0;
                  return (
                    <tr key={x.id} data-testid={`${testPrefix}-row-${x.id}`}>
                      <td className="tabnum">{x.date}</td>
                      <td className="text-right tabnum">{x.milked_animal_count || "—"}</td>
                      <td className="text-right tabnum font-semibold">{x.liters} L</td>
                      <td className="text-right tabnum text-[#8B6914]">{perAn > 0 ? `${perAn.toFixed(2)} L` : "—"}</td>
                      {canSeePrices && <td className="text-right tabnum">{x.unit_price ? `${x.unit_price} ₺` : "—"}</td>}
                      {canSeePrices && <td className="text-right tabnum font-semibold text-[#2D4A36]">{Number(x.revenue || 0).toLocaleString("tr-TR")} ₺</td>}
                      <td className="text-right"><button onClick={() => del(x.id)} className="p-1.5 hover:bg-[#fbeee9] text-[#C45B45] rounded" data-testid={`${testPrefix}-del-${x.id}`}><Trash size={16} /></button></td>
                    </tr>
                  );
                })}
                {!items.length && <tr><td colSpan={canSeePrices ? 7 : 5} className="text-center py-6 text-[#8a938c] text-sm">Henüz kayıt yok</td></tr>}
              </tbody>
            </table>
          </Card>
        </div>
      </div>
    </div>
  );
}

const NORMAL_ACCENT = {
  border: "border-[#E5E2DC]",
  bg: "bg-[#F7F6F3]",
  title: "text-[#1A1D1A]",
  chipBg: "bg-[#E4EEE6]",
  lineLiters: "#2D4A36",
};

const ANTIBIOTIC_ACCENT = {
  border: "border-[#f2d4cb]",
  bg: "bg-[#fbeee9]",
  title: "text-[#8B3A29]",
  chipBg: "bg-[#f7d9d0]",
  lineLiters: "#C45B45",
};

export default function Milk() {
  const { user } = useAuth();
  const canSeePrices =
    user?.role === "owner" ||
    user?.role === "manager" ||
    !!user?.visibility?.milk_pricing;

  return (
    <div data-testid="milk-page">
      <PageHeader
        title="Süt Üretimi"
        subtitle="Normal süt ve antibiyotikli süt kayıtları — her ikisi ayrı takip edilir."
      />

      <MilkComparisonChart canSeePrices={canSeePrices} />

      <MilkSection
        title="Normal Süt"
        subtitle="Piyasaya satılan / tanka aktarılan sağlıklı süt."
        endpoint="/milk"
        testPrefix="milk"
        accent={NORMAL_ACCENT}
        canSeePrices={canSeePrices}
        variantIcon={<Drop size={20} weight="duotone" className="text-[#2D4A36]" />}
      />

      <MilkSection
        title="Antibiyotikli Süt"
        subtitle="Tedavi altındaki hayvanlardan alınan, farklı fiyattan satılan/ayrı değerlendirilen süt."
        endpoint="/antibiotic-milk"
        testPrefix="amilk"
        accent={ANTIBIOTIC_ACCENT}
        canSeePrices={canSeePrices}
        variantIcon={<Warning size={20} weight="duotone" className="text-[#C45B45]" />}
      />
    </div>
  );
}

function MilkComparisonChart({ canSeePrices }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/milk/comparison?months=12").then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;

  const hasAny = data.series.some(x => (x.normal_liters + x.antibiotic_liters) > 0);

  return (
    <div className="mb-8 rounded-lg border border-[#E5E2DC] bg-white overflow-hidden" data-testid="milk-comparison-chart">
      <div className="px-5 py-4 bg-gradient-to-r from-[#F7F6F3] to-[#fbeee9] border-b border-[#E5E2DC] flex items-center gap-3">
        <div className="p-2 rounded-full bg-white shadow-sm">
          <ChartLineUp size={20} weight="duotone" className="text-[#2D4A36]" />
        </div>
        <div className="flex-1">
          <h2 className="heading text-xl text-[#1A1D1A]">Normal vs Antibiyotikli Süt — 12 Aylık Karşılaştırma</h2>
          <p className="text-xs text-[#5e675e]">İki üretim tipini aynı grafikte kıyasla; toplam ve dağılımı gör.</p>
        </div>
      </div>

      <div className="p-5">
        {/* Özet stat şeridi */}
        <div className={`grid gap-3 mb-4 grid-cols-2 md:grid-cols-${canSeePrices ? 5 : 3}`}>
          <div className="p-3 bg-[#F7F6F3] rounded" data-testid="cmp-total-liters">
            <div className="text-[10px] uppercase tracking-widest text-[#8a938c]">Toplam Süt</div>
            <div className="text-lg font-bold text-[#1A1D1A] tabnum">{Number(data.stats.total_liters).toLocaleString("tr-TR")} <span className="text-xs text-[#5e675e]">L</span></div>
          </div>
          <div className="p-3 bg-[#E4EEE6] rounded">
            <div className="text-[10px] uppercase tracking-widest text-[#5e675e]">Normal</div>
            <div className="text-lg font-bold text-[#2D4A36] tabnum">{Number(data.stats.total_normal_liters).toLocaleString("tr-TR")} <span className="text-xs">L</span></div>
          </div>
          <div className="p-3 bg-[#fbeee9] rounded" data-testid="cmp-antibiotic-share">
            <div className="text-[10px] uppercase tracking-widest text-[#5e675e]">Antibiyotikli</div>
            <div className="text-lg font-bold text-[#8B3A29] tabnum">
              {Number(data.stats.total_antibiotic_liters).toLocaleString("tr-TR")} <span className="text-xs">L</span>
              <span className="ml-1 text-[10px] font-normal text-[#5e675e]">({data.stats.antibiotic_pct}%)</span>
            </div>
          </div>
          {canSeePrices && (
            <>
              <div className="p-3 bg-[#F7EBCC] rounded" data-testid="cmp-total-revenue">
                <div className="text-[10px] uppercase tracking-widest text-[#5e675e]">Toplam Gelir</div>
                <div className="text-lg font-bold text-[#8B6914] tabnum">{Number(data.stats.total_revenue).toLocaleString("tr-TR")} <span className="text-xs">₺</span></div>
              </div>
              <div className="p-3 bg-[#F7F6F3] rounded">
                <div className="text-[10px] uppercase tracking-widest text-[#8a938c]">Ay Sayısı</div>
                <div className="text-lg font-bold text-[#1A1D1A] tabnum">12 <span className="text-xs">ay</span></div>
              </div>
            </>
          )}
        </div>

        {!hasAny ? (
          <div className="text-center py-12 text-[#8a938c] italic">Bu dönemde süt üretim kaydı bulunmuyor.</div>
        ) : (
          <div style={{ height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={data.series} margin={{ top: 10, right: 15, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEECE5" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#8a938c" }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: "#8a938c" }} />
                <Tooltip
                  contentStyle={{ borderRadius: 6, border: "1px solid #E5E2DC", fontSize: 12 }}
                  formatter={(v, n) => [`${Number(v).toLocaleString("tr-TR")} L`, n === "normal_liters" ? "Normal" : "Antibiyotikli"]}
                  labelFormatter={(v) => `Ay: ${v}`}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="normal_liters" stackId="a" fill="#2D4A36" name="Normal Süt (L)" />
                <Bar dataKey="antibiotic_liters" stackId="a" fill="#C45B45" name="Antibiyotikli Süt (L)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
